import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Archive, ArchiveRestore, BarChart3, ChevronDown, Copy, DoorOpen, Eye, ExternalLink, FileText, LayoutDashboard, LayoutGrid, Loader2, MessageSquareText, Pencil, Rocket, Search, Settings, Shield, ShieldCheck, Sparkles, Store, Target, Trash2, UserPlus, Users, X } from "lucide-react";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Switch, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import { cn } from "@nexus/ui";
import type { UserItem, UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";
import { isSuperTenantMember } from "@/features/identity-hub/utils/iam.utils";
import { timeAgo } from "@/features/identity-hub/utils/time";
import { statusBadgeClass } from "@/features/identity-hub/utils/status";
import { OrgLogoMark } from "./OrgLogoMark";
import { UserPhotoMark } from "./UserPhotoMark";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { SuperTenantBadge } from "./SuperTenantBadge";
import type { RoleRow } from "./RolesTable";
import { EditPermissionsDialog } from "./EditPermissionsDialog";
import { useUpdateAccessPermissions, useUpdateAccessGroup, useDeleteAccessGroup, useRestoreAccessGroup } from "@/features/identity-hub/hooks/useIdentityHub";
import { usePermissions, useIsOwnAccessGroup } from "@/features/auth/hooks/usePermissions";
import { FEATURE_AREAS, type FeatureArea, type PermDef } from "@/features/identity-hub/lib/permission-catalog";
import { AuditTimeline, useAuditLogBadgeCount } from "./AuditTimeline";
import { ArchivedBadge, RoleTypeBadge, YourRoleBadge } from "./shared/RoleBadges";
import { RowActionsTrigger } from "./shared/RowActionsTrigger";
import { useDrawerClose } from "./shared/drawer/useDrawerClose";
import { DrawerTabStrip } from "./shared/drawer/DrawerTabStrip";
import { DrawerStat } from "./shared/drawer/DrawerStat";
import { MemberActionsMenu } from "./shared/drawer/MemberActionsMenu";

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type DrawerTab = "members" | "permissions" | "audit" | "settings";

// ─── Members tab ─────────────────────────────────────────────────────────────

function MembersTab({
  members,
  accessByUser,
  onJumpToPerson,
  onRemoveMember,
  isRemovingMember,
  removingMemberId,
  onEditProfile,
  onSuspend,
  onUnsuspend,
  onOpenAuditLog,
  isLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: {
  members: UserItem[];
  accessByUser: Map<string, UserAccessEntry[]>;
  onJumpToPerson: (userId: string) => void;
  onRemoveMember?: (userId: string) => void;
  isRemovingMember?: boolean;
  removingMemberId?: string | null;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onOpenAuditLog?: (user: UserItem) => void;
  isLoading?: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const { t } = useTranslation();

  // Members are fetched server-side by accessId and paginated (see the role
  // members query in IdentityHubDashboard). The sentinel auto-loads the next
  // page as the user scrolls the drawer body, so the list reflects the role's
  // true membership rather than only the users on the loaded People page.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onLoadMore?.();
            break;
          }
        }
      },
      { rootMargin: "200px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, onLoadMore, members.length]);

  if (isLoading && members.length === 0) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Users className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">{t("iam.roleDrawer.members.emptyTitle")}</p>
        <p className="text-xs text-muted-foreground">{t("iam.roleDrawer.members.emptyBody")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {members.map((user) => (
        <div
          key={user.id}
          className="group/member flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:bg-muted/40"
        >
          {/* Clickable area → jump to person */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => onJumpToPerson(user.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onJumpToPerson(user.id); } }}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
          >
            <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
              <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="sm" className="shrink-0" />
            </ImagePreviewTrigger>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[13px] font-medium leading-tight text-foreground">
                  {user.displayName}
                </span>
                {isSuperTenantMember(accessByUser.get(user.id)) && <SuperTenantBadge />}
              </div>
              <span className="truncate text-[11px] leading-tight text-muted-foreground">
                {user.email}
              </span>
            </div>
          </div>

          {/* Status badge */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                    statusBadgeClass(user.status),
                  )}
                >
                  {t(`iam.personDrawer.status.${user.status}`, user.status)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                {t(`iam.personDrawer.statusDescription.${user.status}`, {
                  defaultValue: t("iam.personDrawer.statusDescription.default", "Account status."),
                })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Remove from role */}
          {onRemoveMember && (() => {
            const isLastAccess = user.accessIds.length <= 1;
            const isRemoving = isRemovingMember && removingMemberId === user.id;
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={cn("opacity-0 group-hover/member:opacity-100", isRemoving && "opacity-100")}>
                      <button
                        type="button"
                        onClick={() => !isLastAccess && !isRemoving && onRemoveMember(user.id)}
                        disabled={isLastAccess || isRemoving}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-opacity disabled:cursor-not-allowed disabled:opacity-40 hover:bg-destructive/10 hover:text-destructive"
                        aria-label={t("iam.roleDrawer.members.removeFromRole", { name: user.displayName })}
                        aria-busy={isRemoving}
                      >
                        {isRemoving ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-destructive" />
                        ) : (
                          <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                        )}
                      </button>
                    </span>
                  </TooltipTrigger>
                  {isLastAccess && (
                    <TooltipContent side="left">
                      {t("iam.roleDrawer.members.lastAccessTooltip", "Can't revoke — this is the user's only access")}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            );
          })()}

          {/* Actions dropdown */}
          <MemberActionsMenu
            user={user}
            onEditProfile={onEditProfile}
            onOpenAuditLog={onOpenAuditLog}
            onSuspend={onSuspend}
            onUnsuspend={onUnsuspend}
          />
        </div>
      ))}
      {hasMore && (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center py-2 text-[11px] text-muted-foreground"
        >
          {isFetchingMore ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t("iam.roleDrawer.members.loadingMore", {
              defaultValue: "Loading more…",
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── Permissions tab ──────────────────────────────────────────────────────────

type RawPerm = { type: string; method: string; route: string };

// Non-technical admins don't think in HTTP verbs — they think about *what a role
// can do* in each part of the product. Every permission is collapsed into one of
// three plain-language capabilities and grouped under the app section it belongs
// to, mirroring the navigation people already know.
type Capability = "access" | "view" | "manage";

function permCapability(perm: { type: string; method: string }): Capability {
  if (perm.type === "PAGE") return "access";
  return perm.method === "GET" ? "view" : "manage";
}

const CAPABILITY_ORDER: Record<Capability, number> = { access: 0, view: 1, manage: 2 };

const AREA_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  "identity-hub": Users,
  dashboard: LayoutDashboard,
  workspace: LayoutGrid,
  opportunities: Sparkles,
  personas: Target,
  "pre-flight": Rocket,
  marketplace: Store,
  "marketplace-admin": Store,
  overview: Eye,
  drilldown: BarChart3,
  prompts: MessageSquareText,
};

// Resolve a concrete permission (type + method + route) to its catalog entry and
// the section that owns it. Built once from the shared catalog so this read view
// and the editor can never drift apart.
interface PermMeta {
  def: PermDef;
  areaId: string;
  areaLabel: string;
  areaOrder: number;
  groupId: string | null;
  groupLabel: string | null;
  groupOrder: number;
}

const PERM_META_BY_ROUTE = new Map<string, PermMeta>();

function registerFeatureArea(area: FeatureArea, areaOrder: number): void {
  const register = (
    def: PermDef,
    groupId: string | null,
    groupLabel: string | null,
    groupOrder: number,
  ) => {
    PERM_META_BY_ROUTE.set(`${def.type}|${def.method}|${def.route}`, {
      def,
      areaId: area.id,
      areaLabel: area.label,
      areaOrder,
      groupId,
      groupLabel,
      groupOrder,
    });
  };

  if (area.children?.length) {
    area.children.forEach((child, childOrder) => {
      if (child.pagePerm) register(child.pagePerm, child.id, child.label, childOrder);
      child.actionGroups?.forEach((group) =>
        group.perms.forEach((def) => register(def, child.id, child.label, childOrder)),
      );
    });
    return;
  }

  if (area.pagePerm) register(area.pagePerm, null, null, -1);
  area.actionGroups?.forEach((group, groupOrder) =>
    group.perms.forEach((def) => register(def, group.id, group.label, groupOrder)),
  );
}

FEATURE_AREAS.forEach((area, areaOrder) => registerFeatureArea(area, areaOrder));

const lookupPermMeta = (perm: RawPerm): PermMeta | undefined =>
  PERM_META_BY_ROUTE.get(`${perm.type}|${perm.method}|${perm.route}`);

// Escalation ladder styled with a single accent: structural "Access" is a quiet
// ghost, read-only "View" is a soft neutral, and "Manage" — the only capability
// that changes data — carries the brand accent so it's the thing the eye catches.
function useCapabilityMeta(): Record<
  Capability,
  { label: string; tagClass: string; tooltip: string; Icon: ComponentType<{ className?: string }> }
> {
  const { t } = useTranslation();
  return {
    access: {
      label: t("iam.roleDrawer.capability.access.label", "Access"),
      tagClass: "border border-border bg-transparent text-muted-foreground",
      tooltip: t("iam.roleDrawer.capability.access.tooltip", "Can open this section of the app."),
      Icon: DoorOpen,
    },
    view: {
      label: t("iam.roleDrawer.capability.view.label", "View"),
      tagClass: "bg-muted text-muted-foreground",
      tooltip: t("iam.roleDrawer.capability.view.tooltip", "Read-only — can see this, but can't make changes."),
      Icon: Eye,
    },
    manage: {
      label: t("iam.roleDrawer.capability.manage.label", "Manage"),
      tagClass: "bg-primary/10 text-primary",
      tooltip: t("iam.roleDrawer.capability.manage.tooltip", "Can create, edit, or remove — this changes data."),
      Icon: Pencil,
    },
  };
}

function CapabilityTag({ capability }: { capability: Capability }) {
  const { label, tagClass, tooltip, Icon } = useCapabilityMeta()[capability];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Fixed width so the tags line up in a scannable column across rows. */}
        <span
          className={cn(
            "flex w-[74px] shrink-0 cursor-default items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
            tagClass,
          )}
        >
          <Icon className="h-2.5 w-2.5 shrink-0" />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[200px]">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

type PermEntry = { perm: RawPerm; meta?: PermMeta };

function PermissionRow({
  perm,
  meta,
  onRemove,
  isPending,
  disabledTooltip,
  dense,
}: {
  perm: RawPerm;
  meta?: PermMeta;
  onRemove?: (perm: RawPerm) => void;
  isPending?: boolean;
  /** When set, the remove button is disabled and shows this instead of the default tooltip. */
  disabledTooltip?: string;
  dense?: boolean;
}) {
  const { t } = useTranslation();
  const name = meta?.def.name ?? perm.route;
  const description = meta?.def.description;

  return (
    <div
      className={cn(
        "group/perm flex items-center gap-2 rounded-md transition-colors hover:bg-muted/50",
        dense ? "px-1.5 py-1" : "px-2 py-1.5",
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="min-w-0 flex-1 cursor-default truncate text-[12px] font-medium leading-tight text-foreground/90">
            {name}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-[260px]">
          <p className="font-medium">{name}</p>
          {description && <p className="mt-0.5 opacity-80">{description}</p>}
        </TooltipContent>
      </Tooltip>
      <CapabilityTag capability={permCapability(perm)} />
      {onRemove && disabledTooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Plain span (not a disabled button) so the tooltip still fires on hover. */}
            <span className="flex h-6 w-6 shrink-0 cursor-not-allowed items-center justify-center rounded-md opacity-30">
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[220px]">{disabledTooltip}</TooltipContent>
        </Tooltip>
      )}
      {onRemove && !disabledTooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={isPending}
              onClick={() => onRemove(perm)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md opacity-0 transition-opacity hover:bg-destructive/10 group-hover/perm:opacity-100 disabled:pointer-events-none disabled:opacity-30"
              aria-label={t("iam.roleDrawer.removePermissionAria", { name })}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">{t("iam.roleDrawer.removePermissionTooltip")}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// Bulk read-only rows collapse so admins see what changes data first.
const VIEW_CLUSTER_THRESHOLD = 4;

function ViewPermissionCluster({
  entries,
  onRemovePermission,
  isPending,
  disabledTooltip,
}: {
  entries: PermEntry[];
  onRemovePermission?: (perm: RawPerm) => void;
  isPending?: boolean;
  disabledTooltip?: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (entries.length < VIEW_CLUSTER_THRESHOLD) {
    return entries.map(({ perm, meta }) => (
      <PermissionRow
        key={`${perm.type}:${perm.method}:${perm.route}`}
        perm={perm}
        meta={meta}
        onRemove={onRemovePermission}
        isPending={isPending}
        disabledTooltip={disabledTooltip}
        dense
      />
    ));
  }

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex items-center gap-2 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-muted/50"
      >
        <ChevronDown
          className={cn(
            "h-3 w-3 shrink-0 text-muted-foreground/70 transition-transform duration-150",
            !expanded && "-rotate-90",
          )}
        />
        <span className="flex-1 text-[11px] font-medium text-muted-foreground">
          {t("iam.roleDrawer.permissions.viewCluster", { count: entries.length })}
        </span>
        <CapabilityTag capability="view" />
      </button>
      {expanded && (
        <div className="ml-4 flex flex-col border-l border-border/50 pl-2">
          {entries.map(({ perm, meta }) => (
            <PermissionRow
              key={`${perm.type}:${perm.method}:${perm.route}`}
              perm={perm}
              meta={meta}
              onRemove={onRemovePermission}
              isPending={isPending}
              disabledTooltip={disabledTooltip}
              dense
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PermissionSubgroup({
  label,
  entries,
  onRemovePermission,
  isPending,
  disabledTooltip,
}: {
  label: string;
  entries: PermEntry[];
  onRemovePermission?: (perm: RawPerm) => void;
  isPending?: boolean;
  disabledTooltip?: string;
}) {
  const manageEntries = entries.filter(({ perm }) => permCapability(perm) === "manage");
  const viewEntries = entries.filter(({ perm }) => permCapability(perm) === "view");
  const accessEntries = entries.filter(({ perm }) => permCapability(perm) === "access");

  return (
    <div className="flex flex-col gap-0.5 px-3 py-2">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">{label}</p>
      {accessEntries.map(({ perm, meta }) => (
        <PermissionRow
          key={`${perm.type}:${perm.method}:${perm.route}`}
          perm={perm}
          meta={meta}
          onRemove={onRemovePermission}
          isPending={isPending}
          disabledTooltip={disabledTooltip}
        />
      ))}
      {manageEntries.map(({ perm, meta }) => (
        <PermissionRow
          key={`${perm.type}:${perm.method}:${perm.route}`}
          perm={perm}
          meta={meta}
          onRemove={onRemovePermission}
          isPending={isPending}
          disabledTooltip={disabledTooltip}
        />
      ))}
      {viewEntries.length > 0 && (
        <ViewPermissionCluster
          entries={viewEntries}
          onRemovePermission={onRemovePermission}
          isPending={isPending}
          disabledTooltip={disabledTooltip}
        />
      )}
    </div>
  );
}

interface Subgroup {
  id: string;
  label: string;
  order: number;
  perms: PermEntry[];
}

interface AreaGroup {
  id: string;
  label: string;
  pagePerm?: PermEntry;
  subgroups: Subgroup[];
  uncategorized: PermEntry[];
}

function areaGroupEntries(group: AreaGroup): PermEntry[] {
  const entries: PermEntry[] = [];
  if (group.pagePerm) entries.push(group.pagePerm);
  for (const subgroup of group.subgroups) entries.push(...subgroup.perms);
  entries.push(...group.uncategorized);
  return entries;
}

function CapabilitySummaryPills({ entries }: { entries: PermEntry[] }) {
  const { t } = useTranslation();
  const accessCount = entries.filter(({ perm }) => permCapability(perm) === "access").length;
  const viewCount = entries.filter(({ perm }) => permCapability(perm) === "view").length;
  const manageCount = entries.filter(({ perm }) => permCapability(perm) === "manage").length;

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
      {accessCount > 0 && (
        <span className="rounded-full border border-border px-1.5 py-px text-[10px] font-medium tabular-nums text-muted-foreground">
          {t("iam.roleDrawer.capabilitySummary.access")}
        </span>
      )}
      {viewCount > 0 && (
        <span className="rounded-full bg-muted px-1.5 py-px text-[10px] font-medium tabular-nums text-muted-foreground">
          {t("iam.roleDrawer.capabilitySummary.view", { count: viewCount })}
        </span>
      )}
      {manageCount > 0 && (
        <span className="rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-medium tabular-nums text-primary">
          {t("iam.roleDrawer.capabilitySummary.manage", { count: manageCount })}
        </span>
      )}
    </div>
  );
}

function PermissionAreaSection({
  group,
  expanded,
  onToggle,
  onRemovePermission,
  isPending,
  disabledTooltip,
}: {
  group: AreaGroup;
  expanded: boolean;
  onToggle: () => void;
  onRemovePermission?: (perm: RawPerm) => void;
  isPending?: boolean;
  disabledTooltip?: string;
}) {
  const { t } = useTranslation();
  const Icon = AREA_ICONS[group.id] ?? Shield;
  const entries = areaGroupEntries(group);
  const hasSubgroups = group.subgroups.length > 0;

  return (
    <section className="overflow-hidden rounded-lg border border-border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="group/section flex w-full items-center gap-2.5 bg-muted/40 px-3 py-2.5 text-left transition-colors hover:bg-muted/60"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-transform duration-150 group-hover/section:text-muted-foreground",
            !expanded && "-rotate-90",
          )}
        />
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <h4 className="min-w-0 flex-1 truncate text-[13px] font-semibold tracking-tight text-foreground">{group.label}</h4>
        <CapabilitySummaryPills entries={entries} />
      </button>

      <div
        aria-hidden={!expanded}
        className={cn(
          "grid border-t border-border bg-background transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className={cn("overflow-hidden", !expanded && "pointer-events-none")}>
          {group.pagePerm && (
            <div className="border-b border-border/60 bg-muted/20 px-3 py-2.5">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                {t("iam.roleDrawer.permissions.pageAccess")}
              </p>
              <PermissionRow
                perm={group.pagePerm.perm}
                meta={group.pagePerm.meta}
                onRemove={onRemovePermission}
                isPending={isPending}
                disabledTooltip={disabledTooltip}
              />
            </div>
          )}

          {hasSubgroups ? (
            <div className="divide-y divide-border/60">
              {group.subgroups.map((subgroup) => (
                <PermissionSubgroup
                  key={subgroup.id}
                  label={subgroup.label}
                  entries={subgroup.perms}
                  onRemovePermission={onRemovePermission}
                  isPending={isPending}
                  disabledTooltip={disabledTooltip}
                />
              ))}
            </div>
          ) : group.uncategorized.length > 0 ? (
            <div className="flex flex-col gap-0.5 px-3 py-2">
              {group.uncategorized.map(({ perm, meta }) => (
                <PermissionRow
                  key={`${perm.type}:${perm.method}:${perm.route}`}
                  perm={perm}
                  meta={meta}
                  onRemove={onRemovePermission}
                  isPending={isPending}
                  disabledTooltip={disabledTooltip}
                />
              ))}
            </div>
          ) : null}

          {hasSubgroups && group.uncategorized.length > 0 && (
            <div className="border-t border-border/60 px-3 py-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                {t("iam.roleDrawer.permissions.other")}
              </p>
              {group.uncategorized.map(({ perm, meta }) => (
                <PermissionRow
                  key={`${perm.type}:${perm.method}:${perm.route}`}
                  perm={perm}
                  meta={meta}
                  onRemove={onRemovePermission}
                  isPending={isPending}
                  disabledTooltip={disabledTooltip}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

type CapabilityFilter = "all" | "access" | "view" | "manage";

function useCapabilityFilters(): Array<{ id: CapabilityFilter; label: string; tooltip: string }> {
  const { t } = useTranslation();
  return [
    { id: "all", label: t("iam.roleDrawer.capabilityFilters.all.label", "Everything"), tooltip: t("iam.roleDrawer.capabilityFilters.all.tooltip", "Show every permission in this role.") },
    { id: "access", label: t("iam.roleDrawer.capabilityFilters.access.label", "Can open"), tooltip: t("iam.roleDrawer.capabilityFilters.access.tooltip", "Page access — can open this section of the app.") },
    { id: "view", label: t("iam.roleDrawer.capabilityFilters.view.label", "Can view"), tooltip: t("iam.roleDrawer.capabilityFilters.view.tooltip", "Read-only permissions that don't change anything.") },
    { id: "manage", label: t("iam.roleDrawer.capabilityFilters.manage.label", "Can manage"), tooltip: t("iam.roleDrawer.capabilityFilters.manage.tooltip", "Permissions that create, edit, or remove data.") },
  ];
}

function PermissionsTab({
  permissions,
  onRemovePermission,
  isPending,
  disabledTooltip,
}: {
  permissions: RawPerm[];
  onRemovePermission?: (perm: RawPerm) => void;
  isPending?: boolean;
  disabledTooltip?: string;
}) {
  const { t } = useTranslation();
  const capabilityFilters = useCapabilityFilters();
  const [capabilityFilter, setCapabilityFilter] = useState<CapabilityFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  // Per-area expand overrides layered on top of a length-based default, so big
  // roles open collapsed (scannable) while small ones stay fully expanded.
  const [areaOverrides, setAreaOverrides] = useState<Record<string, boolean>>({});

  const actionCount = useMemo(() => permissions.filter((p) => p.type === "ACTION").length, [permissions]);
  const viewCount = useMemo(
    () => permissions.filter((p) => p.type === "ACTION" && p.method === "GET").length,
    [permissions],
  );
  const manageCount = actionCount - viewCount;
  // "Access" (page) permissions are the remainder — surfaced as its own chip so
  // the capability counts reconcile with the total (access + view + manage).
  const accessCount = permissions.length - actionCount;
  const capabilityCounts: Record<CapabilityFilter, number> = {
    all: permissions.length,
    access: accessCount,
    view: viewCount,
    manage: manageCount,
  };

  const q = searchQuery.trim().toLowerCase();

  const areaGroups = useMemo<AreaGroup[]>(() => {
    const byOrder = new Map<number, AreaGroup>();

    const getOrCreateArea = (order: number, id: string, label: string): AreaGroup => {
      let group = byOrder.get(order);
      if (!group) {
        group = { id, label, subgroups: [], uncategorized: [] };
        byOrder.set(order, group);
      }
      return group;
    };

    const getOrCreateSubgroup = (area: AreaGroup, meta: PermMeta): Subgroup => {
      const subgroupId = meta.groupId!;
      let subgroup = area.subgroups.find((g) => g.id === subgroupId);
      if (!subgroup) {
        subgroup = { id: subgroupId, label: meta.groupLabel ?? subgroupId, order: meta.groupOrder, perms: [] };
        area.subgroups.push(subgroup);
      }
      return subgroup;
    };

    for (const perm of permissions) {
      const meta = lookupPermMeta(perm);
      const capability = permCapability(perm);

      if (capabilityFilter !== "all" && capability !== "access" && capability !== capabilityFilter) continue;

      if (q) {
        const haystack = `${meta?.def.name ?? perm.route} ${meta?.def.description ?? ""} ${perm.route}`.toLowerCase();
        if (!haystack.includes(q)) continue;
      }

      const order = meta?.areaOrder ?? Number.MAX_SAFE_INTEGER;
      const area = getOrCreateArea(order, meta?.areaId ?? "other", meta?.areaLabel ?? t("iam.roleDrawer.permissions.other", "Other"));
      const entry: PermEntry = { perm, meta };

      if (perm.type === "PAGE" && !meta?.groupId) {
        area.pagePerm = entry;
      } else if (meta?.groupId) {
        getOrCreateSubgroup(area, meta).perms.push(entry);
      } else {
        area.uncategorized.push(entry);
      }
    }

    const groups = [...byOrder.entries()].sort(([a], [b]) => a - b).map(([, group]) => group);

    const sortEntries = (entries: PermEntry[]) => {
      entries.sort((a, b) => {
        const byCapability = CAPABILITY_ORDER[permCapability(a.perm)] - CAPABILITY_ORDER[permCapability(b.perm)];
        if (byCapability !== 0) return byCapability;
        return (a.meta?.def.name ?? a.perm.route).localeCompare(b.meta?.def.name ?? b.perm.route);
      });
    };

    for (const group of groups) {
      group.subgroups.sort((a, b) => a.order - b.order);
      for (const subgroup of group.subgroups) sortEntries(subgroup.perms);
      sortEntries(group.uncategorized);
    }

    return groups
      .map((group) => ({
        ...group,
        subgroups: group.subgroups.filter((subgroup) => subgroup.perms.length > 0),
      }))
      .filter(
        (group) => group.pagePerm || group.subgroups.length > 0 || group.uncategorized.length > 0,
      );
  }, [permissions, q, capabilityFilter]);

  // A role wider than this many permissions starts collapsed so the whole shape
  // is scannable at a glance; shorter roles stay open.
  const COLLAPSE_THRESHOLD = 16;
  const defaultExpanded = permissions.length <= COLLAPSE_THRESHOLD;
  const isFiltering = q.length > 0 || capabilityFilter !== "all";

  // While filtering, force everything open so matches are never hidden behind a
  // collapsed header.
  const isAreaExpanded = (id: string) => isFiltering || (areaOverrides[id] ?? defaultExpanded);
  const allExpanded = areaGroups.every((group) => areaOverrides[group.id] ?? defaultExpanded);

  const toggleArea = (id: string) =>
    setAreaOverrides((prev) => ({ ...prev, [id]: !(prev[id] ?? defaultExpanded) }));

  const setAllAreas = (value: boolean) =>
    setAreaOverrides(Object.fromEntries(areaGroups.map((group) => [group.id, value])));

  if (permissions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Shield className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">{t("iam.roleDrawer.permissions.emptyTitle")}</p>
        <p className="text-xs text-muted-foreground">{t("iam.roleDrawer.permissions.emptyBody")}</p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("iam.roleDrawer.permissions.searchPlaceholder")}
            className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-3 text-[12px] outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 placeholder:text-muted-foreground"
          />
        </div>

        {/* Capability filter — plain language, not HTTP verbs */}
        {[accessCount, viewCount, manageCount].filter((c) => c > 0).length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {capabilityFilters.filter((filter) => filter.id === "all" || capabilityCounts[filter.id] > 0).map((filter) => {
              const isActive = capabilityFilter === filter.id;
              const count = capabilityCounts[filter.id];
              return (
                <Tooltip key={filter.id}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setCapabilityFilter(filter.id)}
                      aria-pressed={isActive}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                        isActive
                          ? "border-primary/40 bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                      )}
                    >
                      {filter.label}
                      <span className="tabular-nums opacity-60">{count}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[220px]">
                    {filter.tooltip}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        )}

        {/* Section count + expand/collapse-all (hidden while filtering forces open) */}
        {areaGroups.length > 1 && !isFiltering && (
          <div className="flex items-center justify-between px-1">
            <span className="text-[11px] text-muted-foreground">
              {t("iam.roleDrawer.permissions.sectionCount", { count: areaGroups.length })}
            </span>
            <button
              type="button"
              onClick={() => setAllAreas(!allExpanded)}
              className="text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {allExpanded ? t("iam.roleDrawer.permissions.collapseAll") : t("iam.roleDrawer.permissions.expandAll")}
            </button>
          </div>
        )}

        <div className="flex flex-col gap-2">
          {areaGroups.map((group) => (
            <PermissionAreaSection
              key={group.id}
              group={group}
              expanded={isAreaExpanded(group.id)}
              onToggle={() => toggleArea(group.id)}
              onRemovePermission={onRemovePermission}
              isPending={isPending}
              disabledTooltip={disabledTooltip}
            />
          ))}

          {areaGroups.length === 0 && (
            <p className="py-6 text-center text-[12px] text-muted-foreground">
              {t("iam.roleDrawer.permissions.noMatching")}
            </p>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

// ─── Settings tab ─────────────────────────────────────────────────────────────

function SettingsTab({
  role,
  memberCount,
  name,
  description,
  onNameChange,
  onDescChange,
  onDeleteConfirm,
  onRestoreConfirm,
  isDeleting,
  isRestoring,
}: {
  role: RoleRow;
  memberCount: number;
  name: string;
  description: string;
  onNameChange: (v: string) => void;
  onDescChange: (v: string) => void;
  onDeleteConfirm: () => void;
  onRestoreConfirm?: () => void;
  isDeleting: boolean;
  isRestoring?: boolean;
}) {
  const { t } = useTranslation();
  const isSystem = role.type === "system";
  const isArchived = role.isArchived;
  const isReadOnly = isSystem || isArchived;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const DESC_MAX = 500;
  const descOver = description.length > DESC_MAX;

  const readOnlyMessage = isArchived
    ? t(
        "iam.roleDrawer.settings.archivedReadOnly",
        "This role is archived. Restore it to edit name and description.",
      )
    : isSystem
      ? t(
          "iam.roleDrawer.settings.systemReadOnly",
          "Built-in system roles are managed by Nexus and cannot be edited here.",
        )
      : null;

  return (
    <div className="flex flex-col gap-5">
      {readOnlyMessage && (
        <div className="flex items-start gap-2 rounded-md border border-info-subtle-foreground/25 bg-info-subtle px-3 py-2.5 text-xs font-medium text-info-subtle-foreground">
          {isArchived ? (
            <Archive className="mt-px h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ShieldCheck className="mt-px h-4 w-4 shrink-0" aria-hidden />
          )}
          <span>{readOnlyMessage}</span>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">{t("iam.roleDrawer.settings.roleName")}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={isReadOnly}
          className="h-8 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-muted-foreground">{t("iam.roleDrawer.settings.description")}</label>
          <span className={cn("text-[10px] tabular-nums", descOver ? "text-destructive" : "text-muted-foreground/60")}>
            {description.length}/{DESC_MAX}
          </span>
        </div>
        <textarea
          value={description}
          onChange={(e) => onDescChange(e.target.value)}
          disabled={isReadOnly}
          rows={3}
          className={cn(
            "rounded-md border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-1 resize-none disabled:cursor-not-allowed disabled:opacity-50",
            descOver
              ? "border-destructive focus:border-destructive focus:ring-destructive/40"
              : "border-border focus:border-ring focus:ring-ring/40",
          )}
        />
        {descOver && (
          <p className="text-[11px] text-destructive">
            {t("iam.roleDrawer.settings.descriptionTooLong", { max: DESC_MAX })}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <p className="text-xs font-medium text-muted-foreground">
          {t("iam.roleDrawer.settings.security", "Security")}
        </p>
        <div className="rounded-lg border border-dashed border-border bg-muted/10 px-3 py-2.5">
          <p className="text-[13px] font-medium text-muted-foreground">
            {t("iam.roleDrawer.settings.notifyOnAssignment", "Notify on assignment")}
          </p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground/70">
            {t(
              "iam.roleDrawer.settings.notifyOnAssignmentHint",
              "Email admins when a member is added to this role",
            )}
          </p>
          <p className="mt-1.5 text-[11px] font-medium text-muted-foreground/60">
            {t("iam.roleDrawer.settings.notApplicableYet", "Not applicable yet")}
          </p>
        </div>
      </div>

      {isArchived && onRestoreConfirm ? (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
          <p className="mb-2 text-xs font-semibold text-primary">
            {t("iam.roleDrawer.restore.title", "Restore zone")}
          </p>
          <p className="mb-2.5 text-[11px] text-muted-foreground">
            {t(
              "iam.roleDrawer.restore.description",
              "Restoring re-activates this role. Any previously assigned members will need to be granted access again.",
            )}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 border-primary/40 text-primary hover:bg-primary/10 hover:text-primary"
            disabled={isRestoring}
            onClick={onRestoreConfirm}
          >
            <ArchiveRestore className="h-3.5 w-3.5" />
            {isRestoring
              ? t("iam.roleDrawer.restore.restoring", "Restoring…")
              : t("iam.roleDrawer.restore.cta", "Restore role")}
          </Button>
        </div>
      ) : (
        !isSystem && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
            <p className="mb-2 text-xs font-semibold text-destructive">{t("iam.roleDrawer.settings.dangerZone")}</p>
            {confirmDelete ? (
              <div className="flex flex-col gap-2.5">
                <p className="text-[11px] text-muted-foreground">
                  {memberCount > 0
                    ? t("iam.roleDrawer.settings.deleteWarningWithMembers", { count: memberCount })
                    : t("iam.roleDrawer.settings.deleteWarningNoMembers")}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    disabled={isDeleting}
                    onClick={onDeleteConfirm}
                    variant="destructive"
                    size="sm"
                    className="h-6 px-2.5 text-[11px]"
                  >
                    {isDeleting ? t("iam.roleDrawer.settings.deleting") : t("iam.roleDrawer.settings.confirmDelete")}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t("iam.roleDrawer.settings.deleteRole")}
              </Button>
            )}
          </div>
        )
      )}
    </div>
  );
}

// ─── RoleDrawer ───────────────────────────────────────────────────────────────

export interface RoleDrawerProps {
  role: RoleRow | null;
  members: UserItem[];
  /** Members are loaded server-side by accessId and paginated inside the drawer. */
  membersLoading?: boolean;
  hasMoreMembers?: boolean;
  isFetchingMoreMembers?: boolean;
  onLoadMoreMembers?: () => void;
  permissions: Array<{ type: string; method: string; route: string }>;
  allUsers: UserItem[];
  accessByUser: Map<string, UserAccessEntry[]>;
  onClose: () => void;
  onJumpToPerson: (userId: string) => void;
  onRemoveMember?: (userId: string) => void;
  onAddMember?: (userId: string) => void;
  isAddingMember?: boolean;
  addingMemberId?: string | null;
  isRemovingMember?: boolean;
  removingMemberId?: string | null;
  onEditProfile?: (user: UserItem) => void;
  onSuspend?: (user: UserItem) => void;
  onUnsuspend?: (user: UserItem) => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
}

export function RoleDrawer({ role, members, membersLoading, hasMoreMembers, isFetchingMoreMembers, onLoadMoreMembers, permissions, allUsers, accessByUser, onClose, onJumpToPerson, onRemoveMember, onAddMember, isAddingMember, addingMemberId, isRemovingMember, removingMemberId, onEditProfile, onSuspend, onUnsuspend, onOpenAuditLog }: RoleDrawerProps) {
  const { t } = useTranslation();
  const iamPerms = usePermissions();
  const canViewAuditLog = iamPerms.IDENTITY_HUB_VIEW_AUDIT_LOG.isOn;
  const isOwnAccessGroup = useIsOwnAccessGroup(role?.id ?? "");
  const [tab, setTab] = useState<DrawerTab>("members");
  const { visible, handleClose, resetVisible } = useDrawerClose(onClose);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [editPermissionsOpen, setEditPermissionsOpen] = useState(false);
  const [settingsName, setSettingsName] = useState(role?.name ?? "");
  const [settingsDesc, setSettingsDesc] = useState(role?.description ?? "");
  const updatePermissions = useUpdateAccessPermissions();
  const updateGroup = useUpdateAccessGroup();
  const deleteGroup = useDeleteAccessGroup();
  const restoreGroup = useRestoreAccessGroup();
  const prevAddingMember = useRef(false);

  useEffect(() => {
    resetVisible();
    setTab("members");
    setAddMemberOpen(false);
    setAddMemberSearch("");
    setEditPermissionsOpen(false);
    setSettingsName(role?.name ?? "");
    setSettingsDesc(role?.description ?? "");
  }, [role?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (prevAddingMember.current && !isAddingMember) {
      setAddMemberOpen(false);
      setAddMemberSearch("");
    }
    prevAddingMember.current = !!isAddingMember;
  }, [isAddingMember]);

  const eligibleUsers = useMemo(() => {
    if (!role) return [];
    return allUsers.filter((u) => {
      const entries = accessByUser.get(u.id) ?? [];
      return !entries.some((e) => e.access.tenantId === role.tenantId);
    });
  }, [allUsers, accessByUser, role]);

  const filteredEligible = useMemo(() => {
    const q = addMemberSearch.trim().toLowerCase();
    if (!q) return eligibleUsers;
    return eligibleUsers.filter(
      (u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [eligibleUsers, addMemberSearch]);


  // NOTE: this hook must run on every render (incl. when `role` is null) — early-returning
  // before it would change the hook count and crash with "Rendered fewer hooks than expected".
  const auditCount = useAuditLogBadgeCount("access", role?.id, { enabled: canViewAuditLog });

  if (!role) return null;

  const handleRemovePermission = (perm: RawPerm) => {
    if (!role || updatePermissions.isPending) return;
    const remaining = permissions.filter(
      (p) => !(p.type === perm.type && p.method === perm.method && p.route === perm.route),
    );
    updatePermissions.mutate({ accessId: role.id, tenantId: role.tenantId, permissions: remaining });
  };

  const handleSaveSettings = () => {
    if (!role) return;
    // Diff against the persisted role so the audit log only records what
    // actually changed; sending an unchanged name produces a bogus
    // "renamed from X to X" entry.
    const trimmedName = settingsName.trim();
    const trimmedDesc = settingsDesc.trim();
    const nextDesc = trimmedDesc.length > 0 ? trimmedDesc : undefined;
    const currentDesc = role.description ? role.description : undefined;

    const changes: Record<string, unknown> = {};
    if (trimmedName !== role.name) changes.name = trimmedName;
    if (nextDesc !== currentDesc)  changes.description = nextDesc;

    if (Object.keys(changes).length === 0) return;
    updateGroup.mutate({ accessId: role.id, tenantId: role.tenantId, changes });
  };

  const tabs: Array<{ id: DrawerTab; label: string; Icon: ComponentType<{ className?: string }>; count?: number }> = [
    { id: "members",     label: t("iam.roleDrawer.tabs.members"),     Icon: Users,     count: role.memberCount },
    { id: "permissions", label: t("iam.roleDrawer.tabs.permissions"), Icon: Shield,    count: permissions.length },
    { id: "audit",       label: t("iam.roleDrawer.tabs.audit"),       Icon: FileText,  count: auditCount },
    { id: "settings",    label: t("iam.roleDrawer.tabs.settings"),    Icon: Settings },
  ];

  const RoleIcon = role.type === "system" ? ShieldCheck : Shield;

  return (
    <>
      <div
        className={cn(
          "absolute inset-0 z-[9] bg-black/20 xl:hidden duration-200",
          visible ? "animate-in fade-in" : "animate-out fade-out fill-mode-forwards",
        )}
        onClick={handleClose}
        aria-hidden
      />

      <aside
        className={cn(
          "relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card",
          "xl:relative xl:w-[460px] xl:shrink-0",
          "max-xl:absolute max-xl:inset-y-3 max-xl:right-3 max-xl:z-10 max-xl:w-[min(460px,calc(92vw-0.75rem))] max-xl:shadow-[-16px_0_40px_-12px_oklch(0.21_0.01_264/0.25)]",
          "duration-200",
          visible
            ? "animate-in slide-in-from-right-4 fade-in"
            : "animate-out slide-out-to-right-4 fade-out fill-mode-forwards",
        )}
      >
        {/* ── Head ─────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-2.5 border-b border-border/40 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
              <RoleIcon className="h-4.5 w-4.5 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3
                  className={cn(
                    "truncate text-[15px] font-semibold leading-tight",
                    role.isArchived && "line-through decoration-muted-foreground/60",
                  )}
                >
                  {role.name}
                </h3>
                <RoleTypeBadge type={role.type} />
                {role.isArchived && <ArchivedBadge variant="outline" />}
                {isOwnAccessGroup && <YourRoleBadge />}
              </div>
              <div className="flex items-center gap-1.5">
                <ImagePreviewTrigger src={role.tenantLogoUrl} alt={role.tenantName}>
                  <OrgLogoMark name={role.tenantName} logoUrl={role.tenantLogoUrl} size={14} />
                </ImagePreviewTrigger>
                <p className="text-[11px] text-muted-foreground">{role.tenantName}</p>
              </div>
              {role.description && (
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground/70">{role.description}</p>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <RowActionsTrigger />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={() => navigator.clipboard.writeText(role.id)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {t("iam.roleDrawer.copyId")}
                    </DropdownMenuItem>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[220px]">
                    {t("iam.roleDrawer.copyIdTooltip")}
                  </TooltipContent>
                </Tooltip>
                {onOpenAuditLog && canViewAuditLog && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => onOpenAuditLog({ searchPrefill: `${role.name} ${role.tenantName}`, category: "access" })}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {t("iam.roleDrawer.viewAuditLog")}
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[220px]">
                      {t("iam.roleDrawer.viewAuditLogTooltip")}
                    </TooltipContent>
                  </Tooltip>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              onClick={handleClose}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("iam.roleDrawer.closePanel")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* ── Stats row ─────────────────────────────────────── */}
        <div className="grid grid-cols-3 border-b border-border">
          <DrawerStat label={t("iam.roleDrawer.stats.members")} tooltip={t("iam.roleDrawer.stats.membersTooltip")}>
            {role.memberCount}
          </DrawerStat>
          <DrawerStat
            label={t("iam.roleDrawer.stats.permissions")}
            tooltip={role.name === "Super Admin"
              ? t("iam.roleDrawer.stats.permissionsTooltipUnlimited")
              : t("iam.roleDrawer.stats.permissionsTooltip")}
          >
            {role.name === "Super Admin" ? "∞" : role.permissionCount}
          </DrawerStat>
          <DrawerStat label={t("iam.roleDrawer.stats.updated")} tooltip={t("iam.roleDrawer.stats.updatedTooltip")} valueClassName="text-sm font-semibold">
            <span title={role.updatedAt}>{timeAgo(role.updatedAt)}</span>
          </DrawerStat>
        </div>

        {/* ── Tab strip ─────────────────────────────────────── */}
        <DrawerTabStrip tabs={tabs} activeTab={tab} onTabChange={setTab} />

        {/* ── Body ──────────────────────────────────────────── */}
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
          {tab === "members" && (
            <MembersTab
              members={members}
              isLoading={membersLoading}
              hasMore={hasMoreMembers}
              isFetchingMore={isFetchingMoreMembers}
              onLoadMore={onLoadMoreMembers}
              accessByUser={accessByUser}
              onJumpToPerson={onJumpToPerson}
              onRemoveMember={onRemoveMember}
              isRemovingMember={isRemovingMember}
              removingMemberId={removingMemberId}
              onEditProfile={onEditProfile}
              onSuspend={onSuspend}
              onUnsuspend={onUnsuspend}
              onOpenAuditLog={onOpenAuditLog && canViewAuditLog ? (user) => onOpenAuditLog({ searchPrefill: user.email }) : undefined}
            />
          )}

          {tab === "permissions" && (
            <PermissionsTab
              permissions={permissions}
              onRemovePermission={handleRemovePermission}
              isPending={updatePermissions.isPending}
              disabledTooltip={isOwnAccessGroup ? t("iam.validation.selfEditBlocked") : undefined}
            />
          )}

          {tab === "audit" && (
            <AuditTimeline
              scope="access"
              scopeId={role.id}
              hasPermission={canViewAuditLog}
              onEntryClick={onOpenAuditLog ? (id) => onOpenAuditLog({ highlightId: id }) : undefined}
            />
          )}

          {tab === "settings" && (
            <SettingsTab
              role={role}
              memberCount={members.length}
              name={settingsName}
              description={settingsDesc}
              onNameChange={setSettingsName}
              onDescChange={setSettingsDesc}
              onDeleteConfirm={() => {
                deleteGroup.mutate(
                  { accessId: role.id, tenantId: role.tenantId },
                  { onSuccess: handleClose },
                );
              }}
              onRestoreConfirm={() =>
                restoreGroup.mutate({ accessId: role.id, tenantId: role.tenantId })
              }
              isDeleting={deleteGroup.isPending}
              isRestoring={restoreGroup.isPending}
            />
          )}
        </div>

        {/* ── Add member panel ──────────────────────────────── */}
        {addMemberOpen && (
          <div className="animate-in slide-in-from-bottom-2 fade-in duration-200 border-t border-border bg-muted px-4 py-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-medium text-muted-foreground">
                {t("iam.roleDrawer.addMember.title", { role: role.name })}
              </p>
              <button
                type="button"
                onClick={() => setAddMemberOpen(false)}
                className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={addMemberSearch}
                onChange={(e) => setAddMemberSearch(e.target.value)}
                placeholder={t("iam.roleDrawer.addMember.searchPlaceholder")}
                className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-3 text-[12px] outline-none focus:border-ring focus:ring-1 focus:ring-ring/40 placeholder:text-muted-foreground"
              />
            </div>

            {/* User list */}
            <div className="flex max-h-52 flex-col gap-1 overflow-y-auto">
              {filteredEligible.length === 0 ? (
                <p className="py-4 text-center text-[11px] text-muted-foreground">
                  {t("iam.roleDrawer.addMember.empty")}
                </p>
              ) : (
                filteredEligible.map((user) => {
                  const isAddingUser = isAddingMember && addingMemberId === user.id;
                  return (
                  <div
                    key={user.id}
                    role="button"
                    tabIndex={isAddingMember ? -1 : 0}
                    aria-disabled={isAddingMember}
                    aria-busy={isAddingUser}
                    onClick={() => {
                      if (isAddingMember) return;
                      onAddMember?.(user.id);
                    }}
                    onKeyDown={(e) => {
                      if (isAddingMember) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onAddMember?.(user.id);
                      }
                    }}
                    className={cn(
                      "flex cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5",
                      isAddingMember && "cursor-not-allowed",
                      isAddingUser && "border-primary/40 bg-primary/5",
                      isAddingMember && !isAddingUser && "opacity-50",
                    )}
                  >
                    <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
                      <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="sm" className="shrink-0" />
                    </ImagePreviewTrigger>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-[13px] font-medium leading-tight">{user.displayName}</span>
                        {isSuperTenantMember(accessByUser.get(user.id)) && <SuperTenantBadge />}
                      </div>
                      <span className="truncate text-[11px] leading-tight text-muted-foreground">{user.email}</span>
                    </div>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              "flex shrink-0 items-center justify-center",
                              isAddingUser
                                ? "h-5 w-5"
                                : "rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                              !isAddingUser && statusBadgeClass(user.status),
                            )}
                          >
                            {isAddingUser ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                            ) : (
                              t(`iam.personDrawer.status.${user.status}`, user.status)
                            )}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[220px]">
                          {t(`iam.personDrawer.statusDescription.${user.status}`, {
                            defaultValue: t("iam.personDrawer.statusDescription.default", "Account status."),
                          })}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ── Footer ────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          {tab === "settings" ? (
            <Button
              size="sm"
              className="gap-1.5"
              disabled={role.type === "system" || role.isArchived || updateGroup.isPending || !settingsName.trim() || settingsDesc.length > 500}
              onClick={handleSaveSettings}
            >
              {updateGroup.isPending ? t("iam.roleDrawer.settings.saving") : t("common.save")}
            </Button>
          ) : (
            <>
              {isOwnAccessGroup ? (
                <Tooltip>
                  {/* Wrap in a non-disabled span — a disabled button blocks pointer events, which would suppress the tooltip. */}
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button variant="outline" size="sm" className="pointer-events-none gap-1.5" disabled>
                        <Pencil className="h-3.5 w-3.5" />
                        {t("iam.roleDrawer.editPermissions")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[220px]">
                    {t("iam.validation.selfEditBlocked")}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setEditPermissionsOpen(true)}
                  disabled={role.isArchived}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {t("iam.roleDrawer.editPermissions")}
                </Button>
              )}
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => setAddMemberOpen((prev) => !prev)}
                disabled={isAddingMember || role.isArchived}
              >
                {isAddingMember ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <UserPlus className="h-3.5 w-3.5" />
                )}
                {t("iam.roleDrawer.addMemberCta")}
              </Button>
            </>
          )}
        </div>
      </aside>

      {editPermissionsOpen && (
        <EditPermissionsDialog
          open={editPermissionsOpen}
          onOpenChange={setEditPermissionsOpen}
          role={role}
        />
      )}
    </>
  );
}
